/**
 * @fileoverview Mapeo de tipos de vía PTEL → CDAU
 * 
 * Normaliza abreviaturas de tipos de vía usadas en documentos PTEL
 * al formato canónico de CDAU para mejorar la geocodificación.
 * 
 * @example
 * expandTipoVia("C/ Iglesia") // → "CALLE IGLESIA"
 * parseTipoVia("Avda. Andalucía, 5") // → { tipoVia: "AVENIDA", nombreVia: "ANDALUCIA 5" }
 */

/**
 * Mapeo de tipo de vía canónico → variantes reconocidas
 * Incluye abreviaturas con/sin puntuación y formas completas
 */
export const TIPO_VIA_MAP: Record<string, string[]> = {
  'CALLE': ['C/', 'C.', 'CL/', 'CL.', 'CL', 'CALLE'],
  'AVENIDA': ['AV/', 'AV.', 'AVD/', 'AVD.', 'AVDA/', 'AVDA.', 'AVDA', 'AVD', 'AV', 'AVENIDA'],
  'PLAZA': ['PZ/', 'PZ.', 'PZA/', 'PZA.', 'PLZA/', 'PLZA.', 'PLZA', 'PZA', 'PZ', 'PLAZA'],
  'CARRETERA': ['CR/', 'CR.', 'CTRA/', 'CTRA.', 'CRTA/', 'CRTA.', 'CRTA', 'CTRA', 'CR', 'CARRETERA'],
  'CAMINO': ['CMN/', 'CMN.', 'CMNO/', 'CMNO.', 'CMNO', 'CMN', 'CAMINO'],
  'PASEO': ['PS/', 'PS.', 'PSO/', 'PSO.', 'PSO', 'PS', 'PASEO', 'PO/', 'PO.', 'PO'],
  'TRAVESIA': ['TV/', 'TV.', 'TRAV/', 'TRAV.', 'TRVA/', 'TRVA.', 'TRVA', 'TRAV', 'TV', 'TRAVESIA'],
  'GLORIETA': ['GL/', 'GL.', 'GLTA/', 'GLTA.', 'GLTA', 'GL', 'GLORIETA'],
  'RONDA': ['RD/', 'RD.', 'RDA/', 'RDA.', 'RDA', 'RD', 'RONDA'],
  'URBANIZACION': ['URB/', 'URB.', 'URBZ/', 'URBZ.', 'URBZ', 'URB', 'URBANIZACION', 'URBANIZACIÓN'],
  'PARQUE': ['PQ/', 'PQ.', 'PARQ/', 'PARQ.', 'PARQ', 'PQ', 'PARQUE'],
  'POLIGONO': ['PG/', 'PG.', 'POL/', 'POL.', 'POLIG/', 'POLIG.', 'POLIG', 'POL', 'PG', 'POLIGONO', 'POLÍGONO'],
  'PARTIDA': ['PDA/', 'PDA.', 'PART/', 'PART.', 'PART', 'PDA', 'PARTIDA'],
  'BARRIO': ['BR/', 'BR.', 'BRO/', 'BRO.', 'BRO', 'BO/', 'BO.', 'BO', 'BR', 'BARRIO'],
  'CALLEJON': ['CJN/', 'CJN.', 'CLJON/', 'CLJON.', 'CLJON', 'CJN', 'CALLEJON', 'CALLEJÓN'],
  'VEREDA': ['VDA/', 'VDA.', 'VRDA/', 'VRDA.', 'VRDA', 'VDA', 'VEREDA'],
  'PROLONGACION': ['PROL/', 'PROL.', 'PROLOG/', 'PROLOG.', 'PROLOG', 'PROL', 'PROLONGACION', 'PROLONGACIÓN'],
  'CUESTA': ['CTA/', 'CTA.', 'CSTA/', 'CSTA.', 'CSTA', 'CTA', 'CUESTA'],
  'BAJADA': ['BJD/', 'BJD.', 'BJDA/', 'BJDA.', 'BJDA', 'BJD', 'BAJADA'],
  'SUBIDA': ['SBD/', 'SBD.', 'SBDA/', 'SBDA.', 'SBDA', 'SBD', 'SUBIDA'],
  'CALLEJA': ['CLLJ/', 'CLLJ.', 'CLLJ', 'CALLEJA'],
  'LUGAR': ['LG/', 'LG.', 'LUG/', 'LUG.', 'LUG', 'LG', 'LUGAR'],
  'PASAJE': ['PJ/', 'PJ.', 'PSJ/', 'PSJ.', 'PSJ', 'PJ', 'PASAJE'],
};

/** Patrón compilado para matching */
interface TipoViaPattern {
  pattern: string;
  regex: RegExp;
  canonical: string;
}

/** Array ordenado por longitud (longest-match-first) */
let TIPO_VIA_PATTERNS: TipoViaPattern[] = [];

/** Flag para evitar inicialización múltiple */
let patternsInitialized = false;

/**
 * Inicializa patrones ordenados por longitud descendente
 * Debe llamarse antes de usar las funciones de matching
 */
export function initTipoViaPatterns(): void {
  if (patternsInitialized) return;

  const patterns: TipoViaPattern[] = [];

  for (const [canonical, variants] of Object.entries(TIPO_VIA_MAP)) {
    for (const variant of variants) {
      // Escapar caracteres especiales de regex
      const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Patrón: al inicio + límite de palabra o espacio
      const regex = new RegExp(`^${escaped}(?:\\s|$)`, 'i');
      patterns.push({ pattern: variant, regex, canonical });
    }
  }

  // Ordenar por longitud descendente (longest match first)
  patterns.sort((a, b) => b.pattern.length - a.pattern.length);

  TIPO_VIA_PATTERNS = patterns;
  patternsInitialized = true;
}

/**
 * Obtiene tipo canónico desde cualquier variante
 * @param variant Abreviatura o nombre (ej: "AVDA", "Av.", "AVENIDA")
 * @returns Tipo canónico o null si no se reconoce
 */
export function getTipoCanonical(variant: string): string | null {
  initTipoViaPatterns();

  const normalized = variant.trim().toUpperCase();

  for (const [canonical, variants] of Object.entries(TIPO_VIA_MAP)) {
    if (variants.some(v => v.toUpperCase() === normalized)) {
      return canonical;
    }
  }

  return null;
}

/**
 * Detecta y extrae tipo de vía de un texto
 * @param text Dirección completa (ej: "C/ Iglesia, 5")
 * @returns Objeto con tipo de vía detectado y nombre restante
 */
export function parseTipoVia(text: string): {
  tipoVia: string | null;
  nombreVia: string;
} {
  initTipoViaPatterns();

  const trimmed = text.trim();
  if (!trimmed) {
    return { tipoVia: null, nombreVia: '' };
  }

  // Intentar match con cada patrón (ya ordenados por longitud)
  for (const { regex, canonical, pattern } of TIPO_VIA_PATTERNS) {
    if (regex.test(trimmed)) {
      // Extraer nombre quitando el patrón matcheado
      const nombreRaw = trimmed.substring(pattern.length).trim();
      // Normalizar: quitar comas, números de portal al final, mayúsculas
      const nombreVia = normalizeNombreVia(nombreRaw);
      return { tipoVia: canonical, nombreVia };
    }
  }

  // No se detectó tipo de vía
  return { tipoVia: null, nombreVia: normalizeNombreVia(trimmed) };
}

/**
 * Expande abreviaturas de tipo de vía a forma canónica
 * @param text Texto con posible abreviatura (ej: "C/ Iglesia")
 * @returns Texto normalizado (ej: "CALLE IGLESIA")
 */
export function expandTipoVia(text: string): string {
  const { tipoVia, nombreVia } = parseTipoVia(text);

  if (tipoVia && nombreVia) {
    return `${tipoVia} ${nombreVia}`;
  } else if (tipoVia) {
    return tipoVia;
  } else {
    return nombreVia;
  }
}

/**
 * Normaliza nombre de vía: mayúsculas, sin acentos, sin comas
 */
function normalizeNombreVia(nombre: string): string {
  return nombre
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar diacríticos
    .replace(/,/g, ' ')              // Comas → espacios
    .replace(/\s+/g, ' ')            // Múltiples espacios → uno
    .trim();
}

/**
 * Verifica si un texto comienza con un tipo de vía conocido
 */
export function hasTipoVia(text: string): boolean {
  const { tipoVia } = parseTipoVia(text);
  return tipoVia !== null;
}

/**
 * Lista todos los tipos de vía canónicos disponibles
 */
export function getAllTiposVia(): string[] {
  return Object.keys(TIPO_VIA_MAP);
}
